import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import adminChar from '../assets/demo/admin-pi-char.png';
import teacherChar from '../assets/demo/teacher-pi-char.png';
import studentChar from '../assets/demo/student-pi-char.png';
import AppStatic, { isDemoBuild } from '../config/AppStatic';

const ROLE_CARDS = [
    {
        key: 'ADMIN',
        label: 'Admin',
        description: 'Manage schedules, teachers, and rooms.',
        image: adminChar,
        accent: 'hover:border-indigo-200 hover:bg-indigo-50/40',
        imageBg: 'bg-indigo-50',
        arrow: 'group-hover:text-indigo-600',
    },
    {
        key: 'TEACHERS',
        label: 'Teacher',
        description: 'See your classes and timetable with extra tools.',
        image: teacherChar,
        accent: 'hover:border-emerald-200 hover:bg-emerald-50/40',
        imageBg: 'bg-emerald-50',
        arrow: 'group-hover:text-emerald-600',
    },
    {
        key: 'STUDENTS',
        label: 'Student',
        description: 'View your weekly schedule at a glance.',
        image: studentChar,
        accent: 'hover:border-blue-200 hover:bg-blue-50/40',
        imageBg: 'bg-blue-50',
        arrow: 'group-hover:text-blue-600',
    }
];

function DemoOnboardingPage() {
    const navigate = useNavigate();

    if (!isDemoBuild()) {
        return <Navigate to="/schedule" replace />;
    }

    const chooseRole = (roleKey) => {
        AppStatic.setBuildRole(roleKey);
        navigate('/schedule', { replace: true });
    };

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-8 text-slate-900 sm:px-6 lg:px-8">
            <main className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-6xl items-center">
                <div className="grid w-full gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-stretch">
                    <section className="flex flex-col justify-between rounded-[2rem] border border-white bg-white/80 p-8 shadow-sm ring-1 ring-slate-200/70 sm:p-10">
                        <div>
                            <div className="mb-10 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
                                <img src={logo} alt="PI-Schedule logo" className="h-12 w-12 shrink-0" />
                                <div className="leading-none">
                                    <p className="text-lg font-semibold tracking-tight text-slate-900">PI-Schedule</p>
                                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.24em] text-blue-500">Demo</p>
                                </div>
                            </div>

                            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">
                                Welcome
                            </p>
                            <h1 className="max-w-xl text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                                Explore the schedule from every perspective.
                            </h1>
                            <p className="mt-5 max-w-md text-base leading-7 text-slate-500">
                                Try PI-Schedule with sample data. No setup required.
                            </p>
                        </div>

                        <div className="mt-10 grid gap-3 border-t border-slate-200 pt-6 text-sm text-slate-500 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                            <div>
                                <p className="font-semibold text-slate-900">3 portals</p>
                                <p className="mt-1">Admin, Teacher, Student</p>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900">Sample data</p>
                                <p className="mt-1">No setup required</p>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900">Fully featured</p>
                                <p className="mt-1">Explore all features</p>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-[2rem] bg-white p-5 shadow-xl shadow-slate-200/70 ring-1 ring-slate-200 sm:p-7">
                        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-500">
                                    Choose your portal
                                </p>
                                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                                    Continue as
                                </h2>
                            </div>
                            <p className="max-w-xs text-sm leading-6 text-slate-500">
                                You can switch roles later from the demo bar.
                            </p>
                        </div>

                        <div className="grid gap-3">
                            {ROLE_CARDS.map((role) => (
                                <button
                                    key={role.key}
                                    type="button"
                                    onClick={() => chooseRole(role.key)}
                                    className={`group flex w-full items-center gap-4 rounded-3xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${role.accent}`}
                                >
                                    <span className={`flex h-24 w-24 shrink-0 items-end justify-center overflow-hidden rounded-2xl ${role.imageBg}`}>
                                        <img
                                            src={role.image}
                                            alt=""
                                            className="h-24 w-24 object-contain drop-shadow-sm transition-transform duration-200 group-hover:scale-105"
                                        />
                                    </span>

                                    <span className="min-w-0 flex-1">
                                        <span className="block text-lg font-semibold text-slate-950">{role.label}</span>
                                        <span className="mt-1 block text-sm leading-6 text-slate-500">{role.description}</span>
                                    </span>

                                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors ${role.arrow}`}>
                                        <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M3 8h10M9 4l4 4-4 4" />
                                        </svg>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
}

export default DemoOnboardingPage;